import './style.scss';
import { useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import { useMemo, useRef, useState } from 'react';
import {
  createReviews,
  downloadDocxFile,
  downloadDocxTemplate,
  downloadPdfFile,
  downloadPdfTemplate,
  getReviewerInfo,
  getReviewsDetail,
  submitReviews,
  updateReviews,
} from 'apis/reviews.api';
import PropTypes from 'prop-types';
import {
  formatDate,
  getAcadamicLevelShortHand,
  getMissionTypesForReviews,
  isMemberAllowedReview,
  toNonAccentVietnamese,
} from 'utils/func';
import { validateSignature } from 'utils/validation/signatureValidation';
import { toast } from 'layout';
import { TOAST } from 'constant';
import ErrorDialog from 'components/ErrorDialog';
import { Dialog } from 'primereact/dialog';
import { handleTypeData, initialValues, setUndefinedToNull } from 'utils/func/reviews';
import { useNavigate } from 'react-router-dom';
import Loading from 'components/Loading';
import { Menu } from 'primereact/menu';

export default function DynamicAcceptanceForm({
  projectProposalId,
  councilMemberId,
  councilId,
  generalInfo,
  isLoading,
  readOnly,
  formOptions,
}) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [isVisibleSubmitDialog, setIsVisibleSubmitDialog] = useState(false);
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const downloadTemplateButtonRef = useRef();
  const downloadButtonRef = useRef();

  const {
    data: _data,
    isLoading: isGetReviewsDetailLoading,
    refetch,
  } = useQuery(
    ['reviews', councilMemberId, projectProposalId, councilId],
    () => getReviewsDetail(councilMemberId, projectProposalId, councilId),
    {
      enabled: !isLoading,
    }
  );

  const reviewsData = useMemo(() => _data?.data ?? {}, [_data]);

  const dataTemplate = useMemo(
    () =>
      JSON.parse(
        t('lang') === 'vi'
          ? reviewsData?.review?.content ?? null
          : reviewsData?.review?.contentEn ?? null
      ) ?? {},
    [reviewsData, t]
  );

  const {
    data: _dataReviewerInfo,
    isLoading: isGetReviewerInfoLoading,
    refetch: refetchReviewerInfo,
  } = useQuery(['reviewerInfo'], () => getReviewerInfo(projectProposalId, councilId), {
    enabled: !isLoading,
  });

  const reviewerInfo = useMemo(() => {
    const { councilMembers, ...rest } = _dataReviewerInfo?.data ?? {};
    return rest ?? {};
  }, [_dataReviewerInfo]);

  const { mutateAsync: mutateAsyncCreateReviews, isLoading: isCreateReviewsLoading } =
    useMutation(createReviews);
  const { mutateAsync: mutateAsyncUpdateReviews, isLoading: isUpdateReviewsLoading } =
    useMutation(updateReviews);
  const {
    mutateAsync: mutateAsyncSubmitReviews,
    isError: isErrorSubmit,
    error: errorSubmit,
  } = useMutation(submitReviews);

  function getAcadamicLevelShortHand1(opts) {
    const { academicRank, academicDegree, fullname } = opts;

    return `${getAcadamicLevelShortHand(academicRank, academicDegree, t, true)} ${fullname}`;
  }

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    watch,
    reset,
    getValues,
  } = formOptions;

  const { isDirty, dirtyFields } = useFormState({
    control,
  });

  const missionTypes = useMemo(() => getMissionTypesForReviews(t), [t]);

  const existedData = {
    generalInfo: {
      ...generalInfo,
      ownerName: getAcadamicLevelShortHand1({
        academicRank: generalInfo?.projectProposalContacts?.[0]?.academicRank,
        academicDegree: generalInfo?.projectProposalContacts?.[0]?.academicDegree,
        fullname: generalInfo?.projectProposalContacts?.[0]?.fullname,
      }),
      organization: {
        name:
          t('lang') === 'en'
            ? generalInfo?.projectProposalOrganizations?.[0]?.englishName
            : generalInfo?.projectProposalOrganizations?.[0]?.name,
      },
      projectCode: '',
      missionType: missionTypes.find((item) => item.value === generalInfo?.missionType)?.name ?? '',
    },
    reviewerInfo: {
      ...reviewerInfo,
      fullname: getAcadamicLevelShortHand1({
        academicRank: reviewerInfo?.scientificProfile?.academicRank,
        academicDegree: reviewerInfo?.scientificProfile?.academicDegree,
        fullname: reviewerInfo?.fullname,
      }),
      organization: {
        name:
          t('lang') === 'en'
            ? reviewerInfo?.organization?.englishName
            : reviewerInfo?.organization?.name,
      },
      scientificProfile: {
        ...reviewerInfo?.scientificProfile,
        dateOfIssue: reviewerInfo?.scientificProfile?.dateOfIssue
          ? formatDate(reviewerInfo?.scientificProfile?.dateOfIssue)
          : '',
      },
    },
  };

  initialValues({
    control,
    errors,
    reset,
    watch,
    setValue,
    refetchReviewerInfo,
    reviewerInfo,
    existedData,
    readOnly,
  });

  const onSubmit = async (data) => {
    if (!validateSignature(reviewerInfo, t)) return;
    const reviewId = _data?.data?.review?.id;
    const body = {
      projectProposalId,
      councilId,
      councilMemberId,
      reviewTemplateId: _data?.data?.review?.reviewTemplateId,
      reviewValues: {
        ...(setUndefinedToNull(data) ?? {}),
      },
    };
    if (reviewId) {
      body.id = reviewId;
      const res = await mutateAsyncUpdateReviews(body);
      if (res?.status === 200) {
        setIsSubmit(false);
        toast(TOAST.SUCCESS, t('council.review.updateSuccess'));
        reset({}, { keepValues: true });
      } else {
        toast(TOAST.ERROR, t('council.review.updateError'));
      }
    } else {
      const res = await mutateAsyncCreateReviews(body);
      if (res?.status === 200) {
        setIsSubmit(false);
        toast(TOAST.SUCCESS, t('council.review.createSuccess'));
        reset({}, { keepValues: true });
        refetch();
      } else {
        toast(TOAST.ERROR, t('council.review.createError'));
      }
    }
  };

  const {
    refetch: handleDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useQuery(
    ['downloadDocxTemplatePrequalificationReview', projectProposalId, councilId, councilMemberId],
    () => downloadDocxTemplate({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Phieu nhan xet danh gia nghiem thu.docx';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxFile,
    isLoading: isDownloadDocxFileLoading,
    isFetching: isDownloadDocxFileFetching,
  } = useQuery(
    ['downloadDocxFilePrequalificationReview', projectProposalId, councilId, councilMemberId],
    () => downloadDocxFile({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const academicLevelShorthand = getAcadamicLevelShortHand(
            reviewerInfo?.scientificProfile?.academicRank,
            reviewerInfo?.scientificProfile?.academicDegree,
            t,
            true
          );
          const filename = `Phieu nhan xet danh gia nghiem thu - ${academicLevelShorthand}${toNonAccentVietnamese(
            reviewerInfo?.fullname
          )}.docx`;

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: (error) => {
        // 490 is the status code for the error
        // when the scientist has not reviewed the online form yet
        if (error?.response?.status === 490) {
          toast(TOAST.ERROR, t('errorMessage.scientistNotReviewOnlineFormYet'));
        } else {
          toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
        }
      },
    }
  );

  const {
    refetch: handleDownloadPdfFile,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useQuery(
    ['downloadPdfFilePrequalificationReview', projectProposalId, councilId, councilMemberId],
    () => downloadPdfFile({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const academicLevelShorthand = getAcadamicLevelShortHand(
            reviewerInfo?.scientificProfile?.academicRank,
            reviewerInfo?.scientificProfile?.academicDegree,
            t,
            true
          );
          const filename = `Phieu nhan xet danh gia nghiem thu - ${academicLevelShorthand}${toNonAccentVietnamese(
            reviewerInfo?.fullname
          )}.pdf`;

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: async (error) => {
        // 490 is the status code for the error
        // when the scientist has not reviewed the online form yet
        if (error?.response?.status === 490) {
          toast(TOAST.ERROR, t('errorMessage.scientistNotReviewOnlineFormYet'));
        } else {
          toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
        }
      },
    }
  );

  const {
    refetch: handleDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useQuery(
    ['downloadPdfTemplatePrequalificationReview', projectProposalId, councilId, councilMemberId],
    () => downloadPdfTemplate({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Phieu nhan xet danh gia nghiem thu.pdf';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const handleSubmitReview = () => {
    if (!validateSignature(reviewerInfo, t)) return;
    setIsVisibleSubmitDialog(true);
  };

  const onConfirmSubmit = async () => {
    if (!reviewerInfo?.signatureUrl) {
      toast(TOAST.ERROR, t('errorMessage.signatureRequired'));
      return;
    }
    try {
      if (isDirty && Object.keys(dirtyFields).length) {
        const data = getValues();
        const body = {
          id: _data?.data?.review?.id,
          projectProposalId,
          councilId,
          councilMemberId,
          reviewTemplateId: _data?.data?.review?.reviewTemplateId,
          reviewValues: {
            ...(setUndefinedToNull(data) ?? {}),
          },
        };

        await mutateAsyncUpdateReviews(body);
      }

      const submitData = {
        projectProposalId,
        councilId,
        councilMemberId,
      };

      await mutateAsyncSubmitReviews(submitData);

      reset({}, { keepValues: true });
      toast(TOAST.SUCCESS, t('council.review.submitSuccess'), 1000);
      setIsSubmit(true);
      setIsVisibleSubmitDialog(false);
      setTimeout(() => {
        navigate('/project-proposal-defense');
      }, 800);
    } catch {
      setIsVisibleDialogError(true);
    }
  };

  const footerConfirmSubmit = () => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsVisibleSubmitDialog(false)}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirmSubmit}
      />
    </div>
  );
  return (
    <div className="p-5 bg-white dynamic-form">
      {(isLoading ||
        isGetReviewsDetailLoading ||
        isGetReviewerInfoLoading ||
        isCreateReviewsLoading ||
        isUpdateReviewsLoading) && <Loading />}
      <div className="flex justify-content-end mb-4">
        {reviewsData?.review?.id && (
          <Button
            label={t('council.review.exportFile')}
            icon={`pi ${
              isDownloadDocxFileLoading ||
              isDownloadDocxFileFetching ||
              isDownloadPdfFileLoading ||
              isDownloadPdfFileFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadButtonRef.current.toggle(event)}
            disabled={
              isDownloadDocxFileLoading ||
              isDownloadDocxFileFetching ||
              isDownloadPdfFileLoading ||
              isDownloadPdfFileFetching
            }
          />
        )}
        <Button
          label={t('council.review.downloadTemplate')}
          icon={`pi ${
            isDownloadDocxTemplateLoading ||
            isDownloadDocxTemplateFetching ||
            isDownloadPdfTemplateLoading ||
            isDownloadPdfTemplateFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file'
          }`}
          className="block w-auto h-3rem border-none ml-1"
          severity="info"
          onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
          disabled={
            isDownloadDocxTemplateLoading ||
            isDownloadDocxTemplateFetching ||
            isDownloadPdfTemplateLoading ||
            isDownloadPdfTemplateFetching
          }
        />
      </div>
      <Menu
        ref={downloadButtonRef}
        model={[
          {
            label: t('council.review.exportPdf'),
            icon: 'pi pi-file-pdf',
            command: handleDownloadPdfFile,
            disabled: isDownloadPdfFileLoading || isDownloadPdfFileFetching,
          },
          {
            label: t('council.review.exportDocx'),
            icon: 'pi pi-file-word',
            command: handleDownloadDocxFile,
            disabled: isDownloadDocxFileLoading || isDownloadDocxFileFetching,
          },
        ]}
        popup
      />
      <Menu
        ref={downloadTemplateButtonRef}
        model={[
          {
            label: t('council.review.downloadPdfTemplate'),
            icon: 'pi pi-file-pdf',
            command: handleDownloadPdfTemplate,
            disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
          },
          {
            label: t('council.review.downloadDocxTemplate'),
            icon: 'pi pi-file-word',
            command: handleDownloadDocxTemplate,
            disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
          },
        ]}
        popup
      />
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        {Object.keys(dataTemplate).map((key) => handleTypeData(dataTemplate[key]))}

        <div className="flex justify-content-end mt-8">
          <Button
            label={t('formLayout.action.close')}
            severity="danger"
            type="button"
            onClick={() => navigate('/project-proposal-defense')}
            className="mr-2 w-8rem"
          />
          {!readOnly && (
            <>
              <Button
                label={t('formLayout.action.save')}
                severity="info"
                type="submit"
                className="mr-2 w-8rem"
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
              />

              {reviewsData?.review?.id && (
                <Button
                  label={t('formLayout.submit')}
                  severity="success"
                  type="button"
                  onClick={handleSubmitReview}
                  className="w-8rem"
                  disabled={
                    !!reviewsData?.isSubmitted ||
                    reviewsData?.reviewDeadline < new Date().toISOString() ||
                    !isMemberAllowedReview(_dataReviewerInfo?.data?.councilMembers?.[0]) ||
                    isSubmit
                  }
                />
              )}
            </>
          )}
        </div>
      </form>
      {isErrorSubmit && (
        <ErrorDialog
          title={t('council.review.update')}
          isError={isErrorSubmit}
          error={errorSubmit}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <Dialog
        visible={isVisibleSubmitDialog}
        header={<p className="text-blue-500">{t('council.submit.confirmationTitle')}</p>}
        position="center"
        style={{ width: '410px' }}
        onHide={() => setIsVisibleSubmitDialog(false)}
        footer={() => footerConfirmSubmit()}
      >
        <p className="text-lg py-3">{t('council.submit.confirmationSubmitMessage')}</p>
      </Dialog>
    </div>
  );
}

DynamicAcceptanceForm.propTypes = {
  projectProposalId: PropTypes.string.isRequired,
  councilMemberId: PropTypes.string.isRequired,
  councilId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  generalInfo: PropTypes.object,
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  formOptions: PropTypes.object.isRequired,
};

DynamicAcceptanceForm.defaultProps = {
  generalInfo: {},
  isLoading: false,
  readOnly: false,
};
